import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CLAIM } from '@constants/index'
import { ProductBundle } from '@typesApp/Product'
import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { connectorRefreshCart } from '@redux/slices/connectorSlice'
import { setNullifyInProgress } from '@redux/slices/claimStatusSlice'
import { shouldRefreshCartTotals } from '@utils/claimUtil'
import { AutoNullifyHandlerCallback, AutoNullifyHandlerType, AutoNullifyUtil } from '@utils/autoNullifyUtil'
import { usePollClaimRecord } from './usePollClaimRecord'
import { useCreateClaimRecord } from './useCreateClaimRecord'

type AutoNullifyOptions = {
  callback?: () => void
}

type UseAutoNullifyReturn = {
  /**
   * Invoke update cart callback then poll claim record
   * until status allows user to continue
   *
   * @deprecated use waitForNullifyComplete when popups are disabled
   */
  handleAutoNullify: (options?: AutoNullifyOptions) => void

  waitForNullifyComplete: (claimRecord: ClaimRecord, cartProducts: ProductBundle[]) => void
  shouldCheckNullify: (claimRecord: ClaimRecord, cartProducts: ProductBundle[]) => boolean
}

export const useAutoNullify = (): UseAutoNullifyReturn => {
  const dispatch = useDispatch()
  const [isRunning, setRunning] = useState(false)
  const [maxWaitTime, setMaxWaitTime] = useState(CLAIM.UPDATE_CART_POLLING_INTERVAL_MS)

  const [startTime, setStartTime] = useState<Date>()
  const pollClaimRecord = usePollClaimRecord()
  const { patchClaimRecord } = useCreateClaimRecord()

  useEffect(() => {
    if (startTime) {
      dispatch(setNullifyInProgress(true))
      setTimeout(pollClaimRecordForResult, CLAIM.UPDATE_CART_POLLING_INTERVAL_MS)
    }
  }, [startTime])

  const isResultReady = (claimRecord: ClaimRecord): boolean => {
    return AutoNullifyUtil.isResultReady(claimRecord, startTime ?? new Date(), maxWaitTime)
  }

  const handleReady = (claimRecord: ClaimRecord) => {
    if (shouldRefreshCartTotals(claimRecord)) {
      patchClaimRecord({ refreshCartTotalRequired: false }).finally(afterNullify)
    } else {
      afterNullify()
    }
  }

  const afterNullify = () => {
    setRunning(false)
    dispatch(setNullifyInProgress(false))
    dispatch(connectorRefreshCart())
  }

  /**
   * Time exceeded and claim record status not changed
   * no need to refresh cart in this case
   */
  const handleError = () => {
    setRunning(false)
    dispatch(setNullifyInProgress(false))
  }

  const handleResult = (claimRecord: ClaimRecord) => {
    const handlers = {
      ready: () => handleReady(claimRecord),
      nullifyFailed: () => handleReady(claimRecord),
      timeExceeded: () => handleError(),
    } as Record<AutoNullifyHandlerType, AutoNullifyHandlerCallback>

    AutoNullifyUtil.handleResult({ claimRecord, handlers, startTime: startTime ?? new Date(), maxWaitTime })
  }

  const pollClaimRecordForResult = () => {
    pollClaimRecord(isResultReady, handleResult, handleError)
  }

  /**
   * Invoke callback from commerce F/E to apply cart change
   * then poll claim record for status update.
   *
   * FIXME: this will be removed once cart change popups are disabled
   */
  const handleAutoNullify = (options?: AutoNullifyOptions) => {
    setStartTime(new Date())
    typeof options?.callback === 'function' && options.callback()
  }

  const waitForNullifyComplete = (claimRecord: ClaimRecord, cartProducts: ProductBundle[]) => {
    if (!isRunning) {
      setRunning(true)
      setMaxWaitTime(
        isProductAdded(claimRecord, cartProducts) ? CLAIM.MAX_NULLIFY_WAIT_TIME_SHORT : CLAIM.MAX_NULLIFY_WAIT_TIME
      )

      // if nullify already completed, reset refreshCartTotals flag and refresh cart
      // otherwise poll until status is ready
      if (AutoNullifyUtil.isReadyToClaimOrQuote(claimRecord)) {
        dispatch(setNullifyInProgress(true))
        patchClaimRecord({ refreshCartTotalRequired: false }).finally(afterNullify)
      } else {
        setStartTime(new Date())
      }
    }
  }

  const isProductAdded = (claimRecord: ClaimRecord, cartProducts: ProductBundle[]): boolean => {
    return (
      claimRecord.status === ClaimRecordStatus.CLAIM_ACCEPTED &&
      cartProducts.length > (claimRecord.eligibleProducts ?? []).length
    )
  }

  /**
   * NOTE: We should wait for nullify if refreshCartTotalRequired === true or if a new product was
   * added (where refreshCartTotalRequired may not be set)
   */
  const shouldCheckNullify = (claimRecord: ClaimRecord, cartProducts: ProductBundle[]): boolean => {
    return isProductAdded(claimRecord, cartProducts) || shouldRefreshCartTotals(claimRecord)
  }

  return {
    handleAutoNullify,
    waitForNullifyComplete,
    shouldCheckNullify,
  }
}
